"use client";

import { useState } from "react";
import Link from "next/link";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import * as motion from "framer-motion/client";

interface NavLinkProps {
  title: string;
  href?: string;
  children?: Array<{ title: string; href: string; description: string }>;
  items?: Array<{ title: string; href: string; description: string }>;
  isActive?: boolean;
  variant?: "default" | "disclosure";
  openSheet?: boolean;
  setOpenSheet?: (open: boolean) => void;
}

export function NavLink({
  title,
  variant = "default",
  href,
  children,
  isActive,
  openSheet,
  setOpenSheet,
}: NavLinkProps) {
  const [isOpen, setIsOpen] = useState(false);

  if (children) {
    if (variant === "disclosure") {
      return (
        <DisclosureNavLink
          title={title}
          href={href}
          isActive={isActive}
          items={children}
          isOpen={isOpen}
          setIsOpen={() => {
            setOpenSheet?.(false);
            setIsOpen(false);
          }}
        />
      );
    }

    if (variant === "default") {
      return (
        <DefaultNavLink
          title={title}
          href={href}
          isActive={isActive}
          items={children}
          isOpen={isOpen}
          setIsOpen={setIsOpen}
        />
      );
    }
  }

  if (variant === "disclosure") {
    return (
      <div className="border-b">
        <Link
          href={href || "#"}
          className="flex h-11 w-full items-center justify-between py-3 text-sm font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-180"
          onClick={() => setOpenSheet?.(false)}
        >
          {title}
        </Link>
      </div>
    );
  }

  return (
    <Link href={href || "#"}>
      <div
        className={`relative px-4 py-3 text-sm font-medium text-gray-700 transition-colors hover:text-gray-900 ${
          isActive
            ? "text-gray-900 after:absolute after:bottom-0 after:left-1/2 after:h-0.5 after:w-6 after:-translate-x-1/2 after:bg-orange-500"
            : ""
        }`}
      >
        {title}
      </div>
    </Link>
  );
}

const DefaultNavLink = ({
  title,
  href,
  isActive,
  items,
  isOpen,
  setIsOpen,
}: NavLinkProps & {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}) => {
  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          className={`group flex items-center gap-1 px-4 py-3 text-sm font-medium text-gray-700 transition-colors hover:text-gray-900 ${
            isActive ? "text-gray-900" : ""
          }`}
          onClick={() => setIsOpen(!isOpen)}
        >
          {title}
          <ChevronDown
            className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            } group-hover:text-gray-600`}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        className="w-[480px] max-w-screen-sm rounded-lg border border-gray-200 bg-white p-0 shadow-lg"
      >
        <motion.div
          initial={{ y: -8, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="grid w-full grid-cols-2 gap-1 p-2"
        >
          {items?.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className="group block rounded-lg border border-gray-100 bg-gray-50 p-4 transition-all hover:cursor-pointer hover:border-gray-100 hover:bg-gray-100 hover:shadow-xs"
              onClick={() => setIsOpen(false)}
            >
              <h3 className="text-sm font-semibold text-gray-900 group-hover:text-orange-600">
                {item.title}
              </h3>
              <p className="text-xs text-gray-500 group-hover:text-gray-600">
                {item.description}
              </p>
            </Link>
          ))}
        </motion.div>
      </PopoverContent>
    </Popover>
  );
};

const DisclosureNavLink = ({
  title,
  href,
  isActive,
  items,
  isOpen,
  setIsOpen,
}: NavLinkProps & {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}) => {
  return (
    <Accordion type="single" collapsible>
      <AccordionItem value="item-1" className="border-b">
        <AccordionTrigger className="py-3 text-left text-sm font-medium hover:no-underline">
          {title}
        </AccordionTrigger>
        <AccordionContent className="pb-4">
          <div className="space-y-1">
            {items?.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                onClick={() => setIsOpen(false)}
                className="group block rounded-md bg-gray-100 p-2.5 transition-colors hover:bg-gray-200"
              >
                <h3 className="text-sm font-medium text-gray-900 group-hover:text-orange-500">
                  {item.title}
                </h3>
                <p className="text-xs text-gray-600 group-hover:text-gray-700">
                  {item.description}
                </p>
              </Link>
            ))}
          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
